import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { StateMessageComponent } from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AttachmentGroupInfo, AzureBlobUploadResult, FileParameter } from '../../../sdk/sdk';
import { AttachmentService } from '../../../services/attachment.service';
import { FilePreviewService } from '../../../services/file-preview.service';
import { ModalService } from '../../../services/modal.service';

export interface AttachmentUploadDetailsResult {
  files: File[];
  groupId: string;
  description: string;
}

@Component({
  selector: 'app-attachment-upload-details-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, StateMessageComponent],
  templateUrl: './attachment-upload-details-modal.component.html',
})
export class AttachmentUploadDetailsModalComponent implements OnDestroy {
  private readonly attachmentService = inject(AttachmentService);
  private readonly modalService = inject(ModalService);
  private readonly filePreviewService = inject(FilePreviewService);
  readonly activeModal = inject(NgbActiveModal);
  private readonly previewUrls = new Set<string>();

  files: File[] = [];
  opportunityId = '';
  appointmentId = '';
  quoteId = '';

  readonly uploading = signal(false);

  readonly attachmentGroups = signal<AttachmentGroupInfo[]>([]);
  readonly loadingGroups = signal(false);
  readonly loadGroupsError = signal<string | null>(null);

  readonly uploadForm = new FormGroup({
    groupId: new FormControl<string | null>(null),
    description: new FormControl<string>(''),
  });

  constructor() {
    this.fetchAttachmentGroups();
  }

  fetchAttachmentGroups(): void {
    this.loadingGroups.set(true);
    this.loadGroupsError.set(null);
    this.attachmentService.getAttachmentGroups().subscribe({
      next: (groups) => {
        this.attachmentGroups.set(groups ?? []);
        this.loadingGroups.set(false);
      },
      error: () => {
        this.loadGroupsError.set('Unable to load attachment groups.');
        this.loadingGroups.set(false);
      },
    });
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  onUpload(): void {
    if (this.uploadForm.invalid) {
      this.uploadForm.markAllAsTouched();
      return;
    }

    this.uploading.set(true);

    const groupId = this.uploadForm.value.groupId ?? null;
    const description = this.uploadForm.value.description ?? '';

    const uploads = this.files.map((file) => {
      const fileParameter: FileParameter = {
        data: file,
        fileName: file.name,
      };

      return this.attachmentService
        .uploadAttachment(
          this.opportunityId,
          this.appointmentId,
          fileParameter,
          this.quoteId,
          groupId ?? null,
          description
        )
        .pipe(
          catchError((error) => {
            console.error(`Failed to upload file ${file.name}:`, error);
            return of(null);
          })
        );
    });

    forkJoin(uploads).subscribe({
      next: (results: (AzureBlobUploadResult[] | null)[]) => {
        this.uploading.set(false);
        const failed: File[] = [];
        for (let index = 0; index < results.length; index++) {
          const result = results[index];
          if (result === null) {
            failed.push(this.files[index]);
          }
        }

        if (failed.length > 0) {
          const failureModal = this.modalService.openAttachmentUploadFailureModal(
            failed,
            this.opportunityId,
            this.appointmentId,
            groupId,
            description
          );
          failureModal.result
            .then((_result) => {
              this.activeModal.close({ reload: true });
            })
            .catch(() => {
              this.activeModal.close({ reload: true });
            });
        } else {
          this.activeModal.close();
          this.modalService.openAttachmentUploadSuccessModal();
        }
      },
      error: (error) => {
        this.uploading.set(false);
        console.error('Upload error:', error);

        const groupId = this.uploadForm.value.groupId ?? null;
        const description = this.uploadForm.value.description ?? '';
        const failureModal = this.modalService.openAttachmentUploadFailureModal(
          this.files,
          this.opportunityId,
          this.appointmentId,
          groupId,
          description
        );
        failureModal.result
          .then((_result) => {
            this.activeModal.close({ reload: true });
          })
          .catch(() => {
            this.activeModal.close({ reload: true });
          });
      },
    });
  }

  ngOnDestroy(): void {
    this.previewUrls.forEach((url) => this.filePreviewService.revokePreviewUrl(url));
    this.previewUrls.clear();
  }
}
